import {type Point, type Viewport} from '@packages/systems/open-canvas/core';
import {
  type ActionCreator,
  createAction,
} from '@packages/utilities/create-action';
import {
  ViewportChangeSourceNonCanvas,
  ViewportChangeSource,
  ViewportChangeActionType,
  ContinuousViewportChangeActionType,
  CanvasViewportEventTrigger,
} from './types';

export const CANVAS_VIEWPORT_PANNED = 'CANVAS_VIEWPORT_PANNED';
export const CANVAS_VIEWPORT_PANNED_TO_POINT =
  'CANVAS_VIEWPORT_PANNED_TO_POINT';
export const CANVAS_VIEWPORT_RESET_TO_DEFAULT_POSITION =
  'CANVAS_VIEWPORT_RESET_TO_DEFAULT_POSITION';
export const CANVAS_VIEWPORT_ZOOMED = 'CANVAS_VIEWPORT_ZOOMED';
export const CANVAS_VIEWPORT_ZOOMED_STEP = 'CANVAS_VIEWPORT_ZOOMED_STEP';
export const CANVAS_VIEWPORT_ZOOMED_TO_PERCENTAGE =
  'CANVAS_VIEWPORT_ZOOMED_TO_PERCENTAGE';
export const CANVAS_VIEWPORT_FIT_TO_VIEW = 'CANVAS_VIEWPORT_FIT_TO_VIEW';
export const CANVAS_VIEWPORT_CHANGE_STARTED = 'CANVAS_VIEWPORT_CHANGE_STARTED';
export const CANVAS_VIEWPORT_CHANGE_ENDED = 'CANVAS_VIEWPORT_CHANGE_ENDED';
type CanvasViewportPannedPayload = {
  /**
   * The change in viewport position on the x axis in screen pixels
   */
  dx: number;
  /**
   * The change in viewport position on the y axis in screen pixels
   */
  dy: number;
};

export const canvasViewportPanned: ActionCreator<
  typeof CANVAS_VIEWPORT_PANNED,
  CanvasViewportPannedPayload
> = createAction(CANVAS_VIEWPORT_PANNED);

export type CanvasViewportPannedAction = ReturnType<
  typeof canvasViewportPanned
>;

type CanvasViewportPannedToPointPayload = {
  /**
   * The point to pan to, in screen space
   */
  point: Point;
  /**
   * The viewport center, in screen space
   */
  viewportCenter: Point;
};

export const canvasViewportPannedToPoint: ActionCreator<
  typeof CANVAS_VIEWPORT_PANNED_TO_POINT,
  CanvasViewportPannedToPointPayload
> = createAction(CANVAS_VIEWPORT_PANNED_TO_POINT);

export type CanvasViewportPannedToPointAction = ReturnType<
  typeof canvasViewportPannedToPoint
>;

type CanvasViewportResetToDefaultPositionPayload = {
  /**
   * The target viewport position to reset to (the feature's default position and zoom)
   */
  viewport: Viewport;
  /**
   * Analytics data for tracking viewport interactions
   */
  analytics: {
    /**
     * The source of the interaction
     */
    source: ViewportChangeSourceNonCanvas;
  };
};

export const canvasViewportResetToDefaultPosition: ActionCreator<
  typeof CANVAS_VIEWPORT_RESET_TO_DEFAULT_POSITION,
  CanvasViewportResetToDefaultPositionPayload
> = createAction(CANVAS_VIEWPORT_RESET_TO_DEFAULT_POSITION);

export type CanvasViewportResetToDefaultPositionAction = ReturnType<
  typeof canvasViewportResetToDefaultPosition
>;

type CanvasViewportZoomedPayload = {
  /**
   * The zoom factor to apply to the viewport
   */
  zoomFactor: number;
  /**
   * The point around which the zoom is performed, in screen space
   */
  originPoint: Point;
};

export const canvasViewportZoomed: ActionCreator<
  typeof CANVAS_VIEWPORT_ZOOMED,
  CanvasViewportZoomedPayload
> = createAction(CANVAS_VIEWPORT_ZOOMED);

export type CanvasViewportZoomedAction = ReturnType<
  typeof canvasViewportZoomed
>;

type CanvasViewportZoomedStepPayload = {
  /**
   * The viewport center, in screen space
   */
  viewportCenter: Point;
  /**
   * The direction to zoom in or out
   */
  direction: 'in' | 'out';
  /**
   * Analytics data for tracking viewport interactions
   */
  analytics: {
    /**
     * The source of the interaction
     */
    source: ViewportChangeSourceNonCanvas;
  };
};

export const canvasViewportZoomedStep: ActionCreator<
  typeof CANVAS_VIEWPORT_ZOOMED_STEP,
  CanvasViewportZoomedStepPayload
> = createAction(CANVAS_VIEWPORT_ZOOMED_STEP);

export type CanvasViewportZoomedStepAction = ReturnType<
  typeof canvasViewportZoomedStep
>;

type CanvasViewportZoomedToPercentagePayload = {
  /**
   * Zoom value to set, as a percentage
   */
  zoomPercentage: number;
  /**
   * The viewport center, in screen space
   */
  viewportCenter: Point;
  /**
   * Analytics data for tracking viewport interactions
   */
  analytics: {
    /**
     * The type of interaction that caused the zoom
     */
    actionType: Extract<ViewportChangeActionType, 'zoom input' | 'zoom to x'>;
    /**
     * The source of the interaction
     */
    source: ViewportChangeSourceNonCanvas;
  };
};

export const canvasViewportZoomedToPercentage: ActionCreator<
  typeof CANVAS_VIEWPORT_ZOOMED_TO_PERCENTAGE,
  CanvasViewportZoomedToPercentagePayload
> = createAction(CANVAS_VIEWPORT_ZOOMED_TO_PERCENTAGE);

export type CanvasViewportZoomedToPercentageAction = ReturnType<
  typeof canvasViewportZoomedToPercentage
>;

type CanvasViewportFitToViewPayload = {
  /**
   * Current frame bounds in screen space (already transformed by viewport)
   */
  frameRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  /**
   * Open canvas viewport dimensions in screen space
   */
  viewportDimensions: {
    width: number;
    height: number;
  };
  /**
   * Analytics data for tracking viewport interactions
   */
  analytics: {
    /**
     * The source of the interaction
     */
    source: ViewportChangeSourceNonCanvas;
  };
};

export const canvasViewportFitToView: ActionCreator<
  typeof CANVAS_VIEWPORT_FIT_TO_VIEW,
  CanvasViewportFitToViewPayload
> = createAction(CANVAS_VIEWPORT_FIT_TO_VIEW);

export type CanvasViewportFitToViewAction = ReturnType<
  typeof canvasViewportFitToView
>;

type CanvasViewportChangeStartedPayload = {
  /**
   * The trigger that caused the viewport change to start
   */
  trigger: CanvasViewportEventTrigger;
  /**
   * Whether the trigger was initiated from the iframe
   */
  isTriggeredFromIframe: boolean;
  /**
   * Analytics data for tracking viewport interactions
   */
  analytics: {
    /**
     * The type of interaction that started this change session
     */
    actionType: ContinuousViewportChangeActionType;
  };
};

type CanvasViewportChangeEndedPayload = {
  /**
   * The trigger that caused the viewport change to end
   */
  trigger: CanvasViewportEventTrigger;
  /**
   * Analytics data for tracking viewport interactions
   */
  analytics: {
    /**
     * The source of the interaction
     */
    source: ViewportChangeSource;
  };
};

export const canvasViewportChangeStarted: ActionCreator<
  typeof CANVAS_VIEWPORT_CHANGE_STARTED,
  CanvasViewportChangeStartedPayload
> = createAction(CANVAS_VIEWPORT_CHANGE_STARTED);

export type CanvasViewportChangeStartedAction = ReturnType<
  typeof canvasViewportChangeStarted
>;

export const canvasViewportChangeEnded: ActionCreator<
  typeof CANVAS_VIEWPORT_CHANGE_ENDED,
  CanvasViewportChangeEndedPayload
> = createAction(CANVAS_VIEWPORT_CHANGE_ENDED);

export type CanvasViewportChangeEndedAction = ReturnType<
  typeof canvasViewportChangeEnded
>;

export const PAN_MODE_ACTIVATED = 'PAN_MODE_ACTIVATED';
export const PAN_MODE_DEACTIVATED = 'PAN_MODE_DEACTIVATED';

export const panModeActivated: ActionCreator<
  typeof PAN_MODE_ACTIVATED,
  {source: 'space-hold'}
> = createAction(PAN_MODE_ACTIVATED);

export type PanModeActivatedAction = ReturnType<typeof panModeActivated>;

export const panModeDeactivated: ActionCreator<
  typeof PAN_MODE_DEACTIVATED,
  {source: 'space-hold'}
> = createAction(PAN_MODE_DEACTIVATED);

export type PanModeDeactivatedAction = ReturnType<typeof panModeDeactivated>;
